// Backend API base for the public site (redemption validation + activation).
// REACT_APP_API_URL points at the backend's /api base, e.g.
// http://localhost:5000/api. Defaults to localhost for dev.
export const API_BASE = (
  process.env.REACT_APP_API_URL || "http://localhost:5000/api"
).replace(/\/+$/, "");

export interface RedeemState {
  success: boolean;
  state: "valid" | "redeemed" | "expired" | "invalid";
  company_name?: string;
  logo_url?: string | null;
  message?: string;
  validity_days?: number;
  /** Winter Desktops own installer — recipients never go via a store. */
  download_url?: string;
  ms_store_url: string;
}

/**
 * Record that the recipient is downloading — this is what counts their gift as
 * redeemed. keepalive lets the request finish even as the download starts.
 */
export function markDownloaded(token: string): void {
  fetch(`${API_BASE}/redeem/${encodeURIComponent(token)}/download`, {
    method: "POST",
    keepalive: true,
  }).catch(() => {
    /* never block the download on bookkeeping */
  });
}

/** Validate a redemption token for the branded landing page (spec §16). */
export async function fetchRedeem(token: string): Promise<RedeemState> {
  const res = await fetch(`${API_BASE}/redeem/${encodeURIComponent(token)}`);
  if (!res.ok && res.status !== 200) {
    return { success: false, state: "invalid", ms_store_url: "" };
  }
  return (await res.json()) as RedeemState;
}

// ---------------------------------------------------------------------------
// Business gifting page (Outreach spec §7) — token only, no account or payment
// ---------------------------------------------------------------------------

export interface PostcardTheme {
  key: string;
  label: string;
  heading: string;
  sentiment: string;
  motif: string;
  artwork_url: string;
  /** The artwork already has the greeting printed on it. */
  designed: boolean;
}

export interface GiftContext {
  company_name: string;
  logo_url: string | null;
  website_url: string | null;
  postcard_theme: string;
  /** The Administrator-chosen template the firm gifts with. */
  theme: PostcardTheme;
  /** Pre-filled greeting the firm can edit before sending. */
  default_message: string;
  message_max_length: number;
  status: string;
  status_label: string;
  can_gift: boolean;
  unsubscribed: boolean;
  licences_total: number;
  licences_remaining: number;
  invitations_sent: number;
  invitations_redeemed: number;
  subscription_url: string;
}

/** Shown until the backend supplies the firm's template (e.g. mid-deploy). */
const FALLBACK_THEME: PostcardTheme = {
  key: "classic",
  label: "Classic Winter Panda",
  heading: "A Small Gift from Your Team",
  sentiment: "We appreciate your trust and support.",
  motif: "❄  ❄  ❄",
  artwork_url: "/images/image1.png",
  designed: false,
};

const FALLBACK_GIFT_MESSAGE =
  "With Gratitude. Thank you for your continued trust throughout the year.";

/**
 * Defaults for anything an older backend does not send yet: a firm must never
 * meet a broken page because the two sides were deployed minutes apart.
 */
function withDefaults<T extends GiftContext | null | undefined>(gift: T): T {
  if (!gift) return gift;
  return {
    ...gift,
    theme: gift.theme ?? FALLBACK_THEME,
    default_message: gift.default_message ?? FALLBACK_GIFT_MESSAGE,
    message_max_length: gift.message_max_length ?? 500,
  };
}

/** Open a firm's personalised gifting page. Returns null for a bad token. */
export async function fetchGift(token: string): Promise<GiftContext | null> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}`);
    const data = await res.json();
    return data?.success ? withDefaults(data.gift as GiftContext) : null;
  } catch {
    return null;
  }
}

export interface GiftSendResponse {
  success: boolean;
  created?: number;
  invalid?: string[];
  remaining?: number;
  gift?: GiftContext | null;
  error?: string;
  message?: string;
}

/** Send Winter Panda to the firm's recipients — the one-step gifting action. */
export async function sendGifts(
  token: string,
  recipients: string[],
  message: string,
): Promise<GiftSendResponse> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}/send`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ recipients, message }),
    });
    const data = (await res.json()) as GiftSendResponse;
    return { ...data, gift: withDefaults(data.gift) };
  } catch {
    return { success: false, message: "Winter Panda could not be sent. Please try again." };
  }
}

export interface GiftPlan {
  plan_key: string;
  name: string;
  licences: number;
  price_usd: number;
}

/** Paid plans a firm can continue with, keyed off its gifting token. */
export async function fetchGiftPlans(
  token: string,
): Promise<{ plans: GiftPlan[]; current_plan: string | null }> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}/plans`);
    const data = await res.json();
    return data?.success
      ? { plans: data.plans as GiftPlan[], current_plan: data.current_plan ?? null }
      : { plans: [], current_plan: null };
  } catch {
    return { plans: [], current_plan: null };
  }
}

/**
 * Start Stripe Checkout for a paid annual plan straight from the gifting link —
 * no portal account needed (spec §5 "Paid Subscription").
 */
export async function subscribeFromGift(
  token: string,
  planKey: string,
): Promise<{ success: boolean; url?: string; error?: string }> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}/subscribe`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan_key: planKey }),
    });
    return await res.json();
  } catch {
    return { success: false, error: "network" };
  }
}

/** Verify-on-return so a completed payment reflects immediately. */
export async function confirmGiftCheckout(
  token: string,
  sessionId: string,
): Promise<{ success: boolean; gift?: GiftContext | null }> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}/confirm`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ session_id: sessionId }),
    });
    const data = await res.json();
    return { ...data, gift: withDefaults(data.gift) };
  } catch {
    return { success: false };
  }
}

/** One-click unsubscribe from Winter Desktops promotional email. */
export async function unsubscribe(
  token: string,
): Promise<{ success: boolean; company_name?: string }> {
  try {
    const res = await fetch(`${API_BASE}/gift/${encodeURIComponent(token)}/unsubscribe`, {
      method: "POST",
    });
    return await res.json();
  } catch {
    return { success: false };
  }
}
